// Who may open a booking from the calendar, and who may change it. The
// hover on the calendar already tells every member who has the aircraft and
// when, so opening one is for those it concerns. The API makes the same
// calls for changes (routes/bookings.ts); this is what the pages use to
// decide what to offer, so a button is not shown that would only be refused.

export interface BookingViewer {
  userId: string
  // flight.booking.manage_any: the administrators, who may amend history.
  canManageAny: boolean
  canManageOwn: boolean
  // flight.maintenance.write: may open any booking, but not change one they
  // are not a party to, and never a past one.
  canMaintain: boolean
  // Holds an instruct permission in some flight school department; which
  // students is the API's call.
  canInstruct: boolean
}

export interface AccessibleBooking {
  userId: string
  instructorUserId: string | null
  bookingType: string
  endsAt: string
}

const isParty = (booking: AccessibleBooking, viewer: BookingViewer) =>
  booking.userId === viewer.userId || booking.instructorUserId === viewer.userId

// Your own, one you instruct, a lesson (if you teach); everyone's if you
// manage bookings or maintenance.
export function canOpenBooking(booking: AccessibleBooking, viewer: BookingViewer): boolean {
  return (
    isParty(booking, viewer) ||
    viewer.canManageAny ||
    viewer.canMaintain ||
    (booking.bookingType === 'training' && viewer.canInstruct)
  )
}

// Change or cancel it. Once it has ended it is history, and only an
// administrator amends that.
export function canModifyBooking(booking: AccessibleBooking, viewer: BookingViewer, now: Date): boolean {
  if (viewer.canManageAny) return true
  // A maintenance booking is the workshop's: its holder needs the maintenance
  // permission too, as the API asks.
  if (booking.bookingType === 'maintenance' && !viewer.canMaintain) return false
  if (new Date(booking.endsAt).getTime() <= now.getTime()) return false
  if (booking.userId === viewer.userId) return viewer.canManageOwn
  return booking.instructorUserId === viewer.userId || (booking.bookingType === 'training' && viewer.canInstruct)
}
